/**
 * Permission constants and helper functions for access control
 */

import Helper from "./helper";

// Permission constants
export const PERMISSIONS = {
    // System Settings permissions
    SYSTEM_SETTINGS_CUSTOMER_ACCOUNTS_AND_PARTICIPANTS_PROFILE_CONFIGURATION: 'SYSTEM_SETTINGS_CUSTOMER_ACCOUNTS_AND_PARTICIPANTS_PROFILE_CONFIGURATION',
    SYSTEM_SETTINGS_APP_APPEARANCE: 'SYSTEM_SETTINGS_APP_APPEARANCE',
    SYSTEM_SETTINGS_COMPANY_SETTINGS: 'SYSTEM_SETTINGS_COMPANY_SETTINGS',
    SYSTEM_SETTINGS_ATTENDANCE_SETTINGS: 'SYSTEM_SETTINGS_ATTENDANCE_SETTINGS',
    SYSTEM_SETTINGS_NOTIFICATION_SETTINGS: 'SYSTEM_SETTINGS_NOTIFICATION_SETTINGS',
    SYSTEM_SETTINGS_BILLING_SETTINGS: 'SYSTEM_SETTINGS_BILLING_SETTINGS',
    SYSTEM_SETTINGS_ROLES_AND_PERMISSIONS: 'SYSTEM_SETTINGS_ROLES_AND_PERMISSIONS',
    SYSTEM_SETTINGS_CENTERS_CONFIGURATION: 'SYSTEM_SETTINGS_CENTERS_CONFIGURATION',
    SYSTEM_SETTINGS_PRICING_SETTINGS: 'SYSTEM_SETTINGS_PRICING_SETTINGS',

    // Center permissions
    CENTER_VIEW: 'CENTER_VIEW',
    CENTER_ADD: 'CENTER_ADD',
    CENTER_EDIT: 'CENTER_EDIT',
    CENTER_DELETE: 'CENTER_DELETE',

    // Dashboard permissions
    DASHBOARD_VIEW: 'DASHBOARD_VIEW',

    // Booking permissions
    BOOKING_VIEW: 'BOOKING_VIEW',
    BOOKING_ADD: 'BOOKING_ADD',
    BOOKING_EDIT: 'BOOKING_EDIT',
    BOOKING_DELETE: 'BOOKING_DELETE',
    BOOKING_RESCHEDULE: 'BOOKING_RESCHEDULE',
    BOOKING_CANCEL: 'BOOKING_CANCEL',
    BOOKING_CONFIRM: 'BOOKING_CONFIRM',
    BOOKING_DOWNLOAD_PDF: 'BOOKING_DOWNLOAD_PDF',
    BOOKING_VIEW_FEEDBACK: 'BOOKING_VIEW_FEEDBACK',

    // Reports permissions
    REPORTS_GENERATE: 'REPORTS_GENERATE',
    REPORTS_DOWNLOAD: 'REPORTS_DOWNLOAD',
    REPORTS_DELETE: 'REPORTS_DELETE',

    // Calendar permissions
    CALENDAR_VIEW: 'CALENDAR_VIEW',

    // Attendance permissions
    ATTENDANCE_VIEW: 'ATTENDANCE_VIEW',
    ATTENDANCE_MARK: 'ATTENDANCE_MARK',

    // Feed permissions
    FEED_VIEW: 'FEED_VIEW',
    FEED_CREATE_POST: 'FEED_CREATE_POST',
    FEED_EDIT_POST: 'FEED_EDIT_POST',
    FEED_ACTIVE: 'FEED_ACTIVE',
    FEED_INACTIVE: 'FEED_INACTIVE',
    FEED_DELETE: 'FEED_DELETE',

    // Payment permissions
    PAYMENT_VIEW: 'PAYMENT_VIEW',
    PAYMENT_VIEW_PDF: 'PAYMENT_VIEW_PDF',
    PAYMENT_DOWNLOAD_PDF: 'PAYMENT_DOWNLOAD_PDF',
    PAYMENT_EDIT: 'PAYMENT_EDIT',
    PAYMENT_RECORD_PAYMENT: 'PAYMENT_RECORD_PAYMENT',
    PAYMENT_SEND_FEE_REMINDER: 'PAYMENT_SEND_FEE_REMINDER',

    // Account permissions
    ACCOUNT_VIEW: 'ACCOUNT_VIEW',
    ACCOUNT_ADD: 'ACCOUNT_ADD',
    ACCOUNT_EDIT: 'ACCOUNT_EDIT',
    ACCOUNT_ACTIVE: 'ACCOUNT_ACTIVE',
    ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
    ACCOUNT_DELETE: 'ACCOUNT_DELETE',
    ACCOUNT_LINK: 'ACCOUNT_LINK',
    ACCOUNT_DELINK: 'ACCOUNT_DELINK',

    // Participant permissions
    PARTICIPANT_VIEW: 'PARTICIPANT_VIEW',
    PARTICIPANT_ADD: 'PARTICIPANT_ADD',
    PARTICIPANT_EDIT: 'PARTICIPANT_EDIT',
    PARTICIPANT_ACTIVE: 'PARTICIPANT_ACTIVE',
    PARTICIPANT_INACTIVE: 'PARTICIPANT_INACTIVE',
    PARTICIPANT_DELETE: 'PARTICIPANT_DELETE',

    // Package permissions
    PACKAGE_VIEW: 'PACKAGE_VIEW',
    PACKAGE_ADD: 'PACKAGE_ADD',
    PACKAGE_EDIT: 'PACKAGE_EDIT',
    PACKAGE_ACTIVE: 'PACKAGE_ACTIVE',
    PACKAGE_INACTIVE: 'PACKAGE_INACTIVE',
    PACKAGE_DELETE: 'PACKAGE_DELETE',
    PACKAGE_HIDE: 'PACKAGE_HIDE',
    PACKAGE_UNHIDE: 'PACKAGE_UNHIDE',

    // Pricing permissions
    PRICING_VIEW: 'PRICING_VIEW',
    PRICING_ADD: 'PRICING_ADD',
    PRICING_EDIT: 'PRICING_EDIT',
    PRICING_PUBLISH: 'PRICING_PUBLISH',
    PRICING_UNPUBLISH: 'PRICING_UNPUBLISH',
    PRICING_DELETE: 'PRICING_DELETE',

    // Team Role permissions
    TEAM_VIEW: 'TEAM_VIEW',
    TEAM_ADD: 'TEAM_ADD',
    TEAM_EDIT: 'TEAM_EDIT',
    TEAM_ACTIVE: 'TEAM_ACTIVE',
    TEAM_INACTIVE: 'TEAM_INACTIVE',
    TEAM_DELETE: 'TEAM_DELETE',
    TEAM_SEND_INVITE: 'TEAM_SEND_INVITE',
    TEAM_ROLE_EDIT: 'TEAM_ROLE_EDIT',

    // Staff permissions
    STAFF_VIEW: 'STAFF_VIEW',
    STAFF_ADD: 'STAFF_ADD',
    STAFF_EDIT: 'STAFF_EDIT',
    STAFF_DELETE: 'STAFF_DELETE',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Permission display names mapping
export const PERMISSION_DISPLAY_NAMES = {
    // System Settings permissions
    SYSTEM_SETTINGS_CUSTOMER_ACCOUNTS_AND_PARTICIPANTS_PROFILE_CONFIGURATION: 'Customer Accounts & Participants Profile configuration',
    SYSTEM_SETTINGS_APP_APPEARANCE: 'App Appearance (Logo, theme colour)',
    SYSTEM_SETTINGS_COMPANY_SETTINGS: 'Company Settings (Timezone, Currency, Language)',
    SYSTEM_SETTINGS_ATTENDANCE_SETTINGS: 'Attendance Settings',
    SYSTEM_SETTINGS_NOTIFICATION_SETTINGS: 'Notification Settings',
    SYSTEM_SETTINGS_BILLING_SETTINGS: 'Billing Settings',
    SYSTEM_SETTINGS_ROLES_AND_PERMISSIONS: 'Roles & Permissions',

    // Center permissions
    CENTER_VIEW: 'View',
    CENTER_ADD: 'Add',
    CENTER_EDIT: 'Edit',
    CENTER_DELETE: 'Delete',

    // Dashboard permissions
    DASHBOARD_VIEW: 'View',

    // Booking permissions
    BOOKING_VIEW: 'View',
    BOOKING_ADD: 'Add',
    BOOKING_EDIT: 'Edit',
    BOOKING_DELETE: 'Delete',
    BOOKING_RESCHEDULE: 'Reschedule',
    BOOKING_CANCEL: 'Cancel',
    BOOKING_CONFIRM: 'Confirm',
    BOOKING_DOWNLOAD_PDF: 'Download PDF',
    BOOKING_VIEW_FEEDBACK: 'View Feedback',

    // Reports permissions
    REPORTS_GENERATE: 'Generate',
    REPORTS_DOWNLOAD: 'Download',
    REPORTS_DELETE: 'Delete',

    // Calendar permissions
    CALENDAR_VIEW: 'View',

    // Attendance permissions
    ATTENDANCE_VIEW: 'View',
    ATTENDANCE_MARK: 'Mark',

    // Feed permissions
    FEED_VIEW: 'View',
    FEED_CREATE_POST: 'Create Post',
    FEED_EDIT_POST: 'Edit Post',
    FEED_ACTIVE: 'Active',
    FEED_INACTIVE: 'Inactive',
    FEED_DELETE: 'Delete',

    // Payment permissions
    PAYMENT_VIEW: 'View',
    PAYMENT_VIEW_PDF: 'View PDF',
    PAYMENT_DOWNLOAD_PDF: 'Download PDF',
    PAYMENT_EDIT: 'Edit',
    PAYMENT_RECORD_PAYMENT: 'Record Payment',

    // Account permissions
    ACCOUNT_VIEW: 'View',
    ACCOUNT_ADD: 'Add',
    ACCOUNT_EDIT: 'Edit',
    ACCOUNT_ACTIVE: 'Unblock',
    ACCOUNT_INACTIVE: 'Block',
    ACCOUNT_DELETE: 'Delete',
    ACCOUNT_LINK: 'Link',
    ACCOUNT_DELINK: 'Delink',

    // Participant permissions
    PARTICIPANT_VIEW: 'View',
    PARTICIPANT_ADD: 'Add',
    PARTICIPANT_EDIT: 'Edit',
    PARTICIPANT_ACTIVE: 'Active',
    PARTICIPANT_INACTIVE: 'Inactive',
    PARTICIPANT_DELETE: 'Delete',

    // Package permissions
    PACKAGE_VIEW: 'View',
    PACKAGE_ADD: 'Add',
    PACKAGE_EDIT: 'Edit',
    PACKAGE_ACTIVE: 'Active',
    PACKAGE_INACTIVE: 'Inactive',
    PACKAGE_DELETE: 'Delete',
    PACKAGE_HIDE: 'Hide',
    PACKAGE_UNHIDE: 'Unhide',

    // Pricing permissions
    PRICING_VIEW: 'View',
    PRICING_ADD: 'Add',
    PRICING_EDIT: 'Edit',
    PRICING_PUBLISH: 'Publish',
    PRICING_UNPUBLISH: 'Unpublish',
    PRICING_DELETE: 'Delete',

    // Team Role permissions
    TEAM_VIEW: 'View',
    TEAM_ADD: 'Add',
    TEAM_EDIT: 'Edit',
    TEAM_ACTIVE: 'Active',
    TEAM_INACTIVE: 'Inactive',
    TEAM_DELETE: 'Delete',
    TEAM_SEND_INVITE: 'Send Invite',
    TEAM_ROLE_EDIT: 'Role Edit'
} as const;

export function getPermissionDisplayName(permission: string): string {
    return PERMISSION_DISPLAY_NAMES[permission] || permission;
}

export function hasPermissionForEntity(
    firebase: any,
    permission: Permission,
    entityCenterIds: string[]
): boolean {
    if (Helper.isAccount(firebase) || !Helper.isUserLoggedIn(firebase)) {
        return true;
    }

    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return true;
    }

    if (!firebase?.teacher?.permissionMap || !entityCenterIds?.length) {
        return false;
    }

    const permissionMap = firebase.teacher.permissionMap;

    // Check if teacher has the permission for any of the entity's centers
    return entityCenterIds.some(centerId => {
        const centerPermissions = permissionMap[centerId];
        return centerPermissions && centerPermissions.includes(permission);
    });
}

export function hasPermissionForCenter(
    firebase: any,
    permission: Permission,
    centerId: string
): boolean {
    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return true;
    }

    if (!firebase?.teacher?.permissionMap || !centerId) {
        return false;
    }

    const centerPermissions = firebase.teacher.permissionMap[centerId];
    return centerPermissions ? centerPermissions.includes(permission) : false;
}

export function hasAnyPermissionForEntity(
    firebase: any,
    permissions: Permission[],
    entityCenterIds: string[]
): boolean {
    if (Helper.isAccount(firebase) || !Helper.isUserLoggedIn(firebase)) {
        return true;
    }
    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return true;
    }

    if (!firebase?.teacher?.permissionMap || !entityCenterIds?.length || !permissions?.length) {
        return false;
    }

    const permissionMap = firebase.teacher.permissionMap;

    return entityCenterIds.some(centerId => {
        const centerPermissions = permissionMap[centerId];
        if (!centerPermissions) return false;

        return permissions.some(permission => centerPermissions.includes(permission));
    });
}

export function hasAllPermissionsForEntity(
    firebase: any,
    permissions: Permission[],
    entityCenterIds: string[]
): boolean {
    if (Helper.isAccount(firebase) || !Helper.isUserLoggedIn(firebase)) {
        return true;
    }
    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return true;
    }

    if (!firebase?.teacher?.permissionMap || !entityCenterIds?.length || !permissions?.length) {
        return false;
    }

    const permissionMap = firebase.teacher.permissionMap;

    return entityCenterIds.some(centerId => {
        const centerPermissions = permissionMap[centerId];
        if (!centerPermissions) return false;

        return permissions.every(permission => centerPermissions.includes(permission));
    });
}

export function hasPermissionForAllEntityCenters(
    firebase: any,
    permission: Permission,
    entityCenterIds: string[]
): boolean {
    if (Helper.isAccount(firebase) || !Helper.isUserLoggedIn(firebase)) {
        return true;
    }
    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return true;
    }

    if (!firebase?.teacher?.permissionMap || !entityCenterIds?.length) {
        return false;
    }

    const permissionMap = firebase.teacher.permissionMap;

    // Check if teacher has the permission for ALL of the entity's centers
    return entityCenterIds.every(centerId => {
        const centerPermissions = permissionMap[centerId];
        return centerPermissions && centerPermissions.includes(permission);
    });
}

/**
 * Gets all permissions for a specific center
 * @param firebase - Firebase object containing teacher data
 * @param centerId - The center ID to get permissions for
 * @returns string[] - Array of permissions for the center
 */
export function getPermissionsForCenter(firebase: any, centerId: string): string[] {
    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return Object.values(PERMISSIONS);
    }

    if (!firebase?.teacher?.permissionMap || !centerId) {
        return [];
    }

    return firebase.teacher.permissionMap[centerId] || [];
}

export function getAllPermissions(firebase: any): string[] {
    // Super admin has all permissions
    if (firebase?.teacher?.isSuperAdmin) {
        return Object.values(PERMISSIONS);
    }

    if (!firebase?.teacher?.permissionMap) {
        return [];
    }

    const allPermissions = new Set<string>();
    const permissionMap = firebase.teacher.permissionMap;

    Object.values(permissionMap).forEach((permissions: any) => {
        if (Array.isArray(permissions)) {
            permissions.forEach(permission => allPermissions.add(permission));
        }
    });

    return Array.from(allPermissions);
}
